import './ScrollArea.css'

import { ReactNode, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { cx } from '../utilities/cx'
import { defaultOverlayRootName, getOverlayRootElement } from './OverlayRoot'

export type ScrollPosition = {
  horizontal?: number | 'right' | 'left'
  vertical?: number | 'top' | 'bottom'
}

export type ScrollBarVisibility = {
  vertical: boolean
  horizontal: boolean
}

export type ScrollState = {
  horizontal: number
  vertical: number
  outerWidth: number
  outerHeight: number
  innerWidth: number
  innerHeight: number
}

export type ScrollAreaProps = {
  className?: string
  horizontal?: boolean
  vertical?: boolean
  fitParent?: boolean
  scroll?: ScrollPosition
  onScroll?: (scrollState: ScrollState) => void
  onScrollBarVisibility?: (scrollBarVisibility: ScrollBarVisibility) => void
  overlayRootName?: string
  children?: ReactNode
}

export const ScrollArea = (props: ScrollAreaProps) => {
  const {
    className,
    fitParent,
    scroll,
    onScroll,
    onScrollBarVisibility,
    overlayRootName = defaultOverlayRootName,
    children,
  } = props

  const horizontal = props.horizontal
  const vertical = props.vertical ?? !horizontal

  const verticalScrollHandleRef = useRef<HTMLDivElement>(null)
  const horizontalScrollHandleRef = useRef<HTMLDivElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const innerRef = useRef<HTMLDivElement>(null)

  const [showTopShadow, setShowTopShadow] = useState(false)
  const [showBottomShadow, setShowBottomShadow] = useState(false)
  const [showLeftShadow, setShowLeftShadow] = useState(false)
  const [showRightShadow, setShowRightShadow] = useState(false)
  const [showDragOverlay, setShowDragOverlay] = useState(false)

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    if (scroll?.vertical) {
      if (scroll.vertical === 'top') {
        el.scrollTop = 0
      } else if (scroll.vertical === 'bottom') {
        el.scrollTop = el.scrollHeight - el.clientHeight
      } else {
        el.scrollTop = scroll.vertical
      }
    }
    if (scroll?.horizontal) {
      if (scroll.horizontal === 'left') {
        el.scrollLeft = 0
      } else if (scroll.horizontal === 'right') {
        el.scrollLeft = el.scrollWidth - el.clientWidth
      } else {
        el.scrollLeft = scroll.horizontal
      }
    }
  }, [scroll])

  useEffect(() => {
    const scrollEl = scrollRef.current
    const verticalHandleEl = verticalScrollHandleRef.current
    const horizontalHandleEl = horizontalScrollHandleRef.current
    const innerEl = innerRef.current
    if (!scrollEl || !innerEl || (!verticalHandleEl && !horizontalHandleEl)) return

    let scrollHandlesHideTimeoutId: ReturnType<typeof setTimeout>

    const hideScrollHandlesLater = () => {
      clearTimeout(scrollHandlesHideTimeoutId)
      scrollHandlesHideTimeoutId = setTimeout(() => {
        if (verticalHandleEl) verticalHandleEl.style.opacity = '0'
        if (horizontalHandleEl) horizontalHandleEl.style.opacity = '0'
        onScrollBarVisibility?.({ vertical: false, horizontal: false })
      }, 2000)
    }

    let updateRafId: number
    const handleScroll = () => {
      cancelAnimationFrame(updateRafId)
      updateRafId = requestAnimationFrame(() => {
        if (!scrollEl) return

        setShowTopShadow(Math.round(scrollEl.scrollTop) !== 0)
        setShowBottomShadow(
          Math.round(scrollEl.scrollTop + scrollEl.clientHeight) !== scrollEl.scrollHeight,
        )
        setShowLeftShadow(Math.round(scrollEl.scrollLeft) !== 0)
        setShowRightShadow(
          Math.round(scrollEl.scrollLeft + scrollEl.clientWidth) !== scrollEl.scrollWidth,
        )

        onScroll?.({
          vertical: scrollEl.scrollTop,
          horizontal: scrollEl.scrollLeft,
          outerWidth: scrollEl.clientWidth,
          outerHeight: scrollEl.clientHeight,
          innerWidth: innerEl.scrollWidth,
          innerHeight: innerEl.scrollHeight,
        })

        const showVerticalHandle = !!vertical && scrollEl.scrollHeight > scrollEl.clientHeight
        const showHorizontalHandle = !!horizontal && scrollEl.scrollWidth > scrollEl.clientWidth

        if (verticalHandleEl) {
          verticalHandleEl.style.display = showVerticalHandle ? 'block' : 'none'
        }
        if (horizontalHandleEl) {
          horizontalHandleEl.style.display = showHorizontalHandle ? 'block' : 'none'
        }

        if (showVerticalHandle && verticalHandleEl) {
          const handleHeight =
            (scrollEl.clientHeight / scrollEl.scrollHeight) * scrollEl.clientHeight
          verticalHandleEl.style.opacity = '1'
          verticalHandleEl.style.height = `${handleHeight}px`
          verticalHandleEl.style.top = `${(scrollEl.scrollTop / scrollEl.scrollHeight) * scrollEl.clientHeight}px`
        }

        if (showHorizontalHandle && horizontalHandleEl) {
          const handleWidth = (scrollEl.clientWidth / scrollEl.scrollWidth) * scrollEl.clientWidth
          horizontalHandleEl.style.opacity = '1'
          horizontalHandleEl.style.width = `${handleWidth}px`
          horizontalHandleEl.style.left = `${(scrollEl.scrollLeft / scrollEl.scrollWidth) * scrollEl.clientWidth}px`
        }

        onScrollBarVisibility?.({ vertical: showVerticalHandle, horizontal: showHorizontalHandle })
        hideScrollHandlesLater()
      })
    }

    let dragRafId: number
    let isDragging = false
    let startScrollTop = 0
    let startScrollLeft = 0
    let startX = 0
    let startY = 0
    let currentX = 0
    let currentY = 0

    const handleMouseDown = (direction: 'vertical' | 'horizontal', event: MouseEvent) => {
      isDragging = true
      startX = event.clientX
      startY = event.clientY
      startScrollTop = scrollEl.scrollTop
      startScrollLeft = scrollEl.scrollLeft

      const update = () => {
        if (!isDragging) {
          setShowDragOverlay(false)
          return
        }
        setShowDragOverlay(true)

        if (direction === 'vertical') {
          const deltaY = currentY - startY
          const scrollOffset = (deltaY / scrollEl.clientHeight) * scrollEl.scrollHeight
          scrollEl.scrollTop = startScrollTop + scrollOffset
          verticalHandleEl!.classList.add('scroll-area__handle--active')
        }

        if (direction === 'horizontal') {
          const deltaX = currentX - startX
          const scrollOffset = (deltaX / scrollEl.clientWidth) * scrollEl.scrollWidth
          scrollEl.scrollLeft = startScrollLeft + scrollOffset
          horizontalHandleEl!.classList.add('scroll-area__handle--active')
        }

        cancelAnimationFrame(dragRafId)
        dragRafId = requestAnimationFrame(update)
      }
      update()
    }

    const handleVerticalMouseDown = (event: MouseEvent) => handleMouseDown('vertical', event)
    const handleHorizontalMouseDown = (event: MouseEvent) => handleMouseDown('horizontal', event)

    let revealRafId: number
    const handleMouseMove = (event: MouseEvent) => {
      currentX = event.clientX
      currentY = event.clientY

      if (isDragging) return

      cancelAnimationFrame(revealRafId)
      revealRafId = requestAnimationFrame(() => {
        const showHandleIfNear = (handleEl: HTMLDivElement) => {
          const handleRect = handleEl.getBoundingClientRect()
          const scrollRect = scrollEl.getBoundingClientRect()

          const handleMargin = 48
          const isNearHandle =
            currentX >= handleRect.x - handleMargin &&
            currentX <= handleRect.x + handleRect.width + handleMargin &&
            currentY >= handleRect.y - handleMargin &&
            currentY <= handleRect.y + handleRect.height + handleMargin

          const isOverScrollArea =
            currentX >= scrollRect.x &&
            currentX <= scrollRect.x + scrollRect.width &&
            currentY >= scrollRect.y &&
            currentY <= scrollRect.y + scrollRect.height

          if (isNearHandle || isOverScrollArea) handleEl.style.opacity = '1'

          const showVerticalHandle = !!vertical && scrollEl.scrollHeight > scrollEl.clientHeight
          const showHorizontalHandle = !!horizontal && scrollEl.scrollWidth > scrollEl.clientWidth

          onScrollBarVisibility?.({
            vertical: showVerticalHandle,
            horizontal: showHorizontalHandle,
          })
          hideScrollHandlesLater()
        }
        if (vertical && verticalHandleEl) showHandleIfNear(verticalHandleEl)
        if (horizontal && horizontalHandleEl) showHandleIfNear(horizontalHandleEl)
      })
    }

    const handleMouseUp = () => {
      isDragging = false
      if (horizontalHandleEl) horizontalHandleEl.classList.remove('scroll-area__handle--active')
      if (verticalHandleEl) verticalHandleEl.classList.remove('scroll-area__handle--active')
    }

    handleScroll()

    scrollEl.addEventListener('scroll', handleScroll, { passive: true })
    verticalHandleEl?.addEventListener('mousedown', handleVerticalMouseDown, { passive: true })
    horizontalHandleEl?.addEventListener('mousedown', handleHorizontalMouseDown, { passive: true })
    window.addEventListener('mousemove', handleMouseMove, { passive: true })
    window.addEventListener('mouseup', handleMouseUp, { passive: true })
    const resizeObserver = new ResizeObserver(handleScroll)
    resizeObserver.observe(scrollEl)

    return () => {
      clearTimeout(scrollHandlesHideTimeoutId)
      cancelAnimationFrame(updateRafId)
      cancelAnimationFrame(dragRafId)
      cancelAnimationFrame(revealRafId)

      scrollEl.removeEventListener('scroll', handleScroll)
      verticalHandleEl?.removeEventListener('mousedown', handleVerticalMouseDown)
      horizontalHandleEl?.removeEventListener('mousedown', handleHorizontalMouseDown)
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
      resizeObserver.disconnect()
    }
    // Intentionally limited deps - only re-run when children change
  }, [children])

  const rootClasses = cx('scroll-area', { 'scroll-area--fit-parent': fitParent }, className)

  const scrollClasses = cx('scroll-area__scroll', {
    'scroll-area__scroll--horizontal': horizontal,
    'scroll-area__scroll--vertical': vertical,
  })

  const innerClasses = cx('scroll-area__inner', {
    'scroll-area__inner--horizontal': horizontal,
    'scroll-area__inner--vertical': vertical,
    'scroll-area__inner--fit-parent': fitParent,
  })

  return (
    <div className={rootClasses}>
      <div ref={scrollRef} className={scrollClasses} tabIndex={0}>
        <div ref={innerRef} className={innerClasses}>
          {children}
        </div>
      </div>

      {vertical && (
        <>
          <div
            className={cx('scroll-area__shadow scroll-area__shadow--top', {
              'scroll-area__shadow--visible': showTopShadow,
            })}
          />
          <div
            className={cx('scroll-area__shadow scroll-area__shadow--bottom', {
              'scroll-area__shadow--visible': showBottomShadow,
            })}
          />
        </>
      )}

      {horizontal && (
        <>
          <div
            className={cx('scroll-area__shadow scroll-area__shadow--left', {
              'scroll-area__shadow--visible': showLeftShadow,
            })}
          />
          <div
            className={cx('scroll-area__shadow scroll-area__shadow--right', {
              'scroll-area__shadow--visible': showRightShadow,
            })}
          />
        </>
      )}

      {vertical && <div ref={verticalScrollHandleRef} className="scroll-area__handle" />}
      {horizontal && <div ref={horizontalScrollHandleRef} className="scroll-area__handle" />}

      {showDragOverlay &&
        createPortal(
          <div className="scroll-area__drag-overlay" />,
          getOverlayRootElement(overlayRootName)!,
        )}
    </div>
  )
}
